import { Button, Space } from 'antd';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { NotificationContext } from '@client/context/NotificationContext';
import { useError } from '@client/hooks/useError';
import { useFeatureQueries } from '@client/queries/useFeatureQueries';
import { useSchedulerQueries } from '@client/queries/useSchedulerQueries';
import { getSubscriptionsByFeature } from '@client/services/subscription.service';
import { useLoading } from '@client/store/loading.store';
import { ERoutePaths } from '@client/types';
import {
  EErrorType,
  CSchedulerEntityPaths,
  type IFeature,
  type TErrorData,
  EStatus,
} from '@common/types';

export const useFeature = () => {
  const {
    useUserFeature,
    useDeleteFeature,
    useUpdateFeature,
    useCreateFeature,
    useTogglePublic,
    useToggleActive,
  } = useFeatureQueries();
  const { useDeleteEntitySchedulers } = useSchedulerQueries();
  const { notificationApi } = useContext(NotificationContext);
  const { t } = useTranslation();
  const { setError } = useError();
  const params = useParams();
  // Query for user features
  const {
    data: feature,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useUserFeature(params.featureId);

  const { setLoading } = useLoading(false);
  const navigate = useNavigate();

  // Mutation for deactivating a feature
  const deleteMutation = useDeleteFeature();
  const deleteAllSchedulers = useDeleteEntitySchedulers<IFeature>(
    CSchedulerEntityPaths.FEATURES,
  );
  const createMutation = useCreateFeature();
  const updateMutation = useUpdateFeature();
  const publicMutation = useTogglePublic();
  const activeMutation = useToggleActive();

  useEffect(() => {
    if (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.DOCUMENT_NOT_FOUND,
      });

      navigate(ERoutePaths.FEATURES);
    }
  }, [error, navigate, setError]);

  const onDelete = async (entity: IFeature): Promise<void> => {
    try {
      setLoading(true);
      await Promise.all([
        deleteMutation.mutateAsync(entity.id as string),
        deleteAllSchedulers.mutateAsync(entity.id as string),
      ]);

      navigate(ERoutePaths.FEATURES);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DELETE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = async (
    id: IFeature['id'],
    data: IFeature,
  ): Promise<void> => {
    try {
      setLoading(true);
      await updateMutation.mutateAsync({ id, data });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_UPDATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onCreate = async (data: IFeature): Promise<IFeature> => {
    try {
      setLoading(true);
      return createMutation.mutateAsync(data);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_CREATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onPublic = async (data: IFeature): Promise<void> => {
    try {
      setLoading(true);
      await publicMutation.mutateAsync(data);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_PUBLIC,
      });
    } finally {
      setLoading(false);
    }
  };

  const onActivate = async (data: IFeature): Promise<void> => {
    try {
      setLoading(true);

      if (data.status === EStatus.ACTIVE) {
        const subscriptions = await getSubscriptionsByFeature(
          data.id as string,
        );

        if (subscriptions.length > 0) {
          const subscription = subscriptions[0];
          const key = `deactivate-blocked-${data.id}`;

          notificationApi?.warning({
            key,
            title: t('feature.msg.deactivateBlocked'),
            description: t('feature.msg.deactivateBlockedDescription', {
              subscriptionName: subscription.title,
            }),
            actions: (
              <Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    notificationApi.destroy(key);
                    navigate(
                      ERoutePaths.SUBSCRIPTION_EDIT.replace(
                        ':subscriptionId',
                        subscription.id as string,
                      ),
                    );
                  }}
                >
                  {t('feature.actions.navigateToSubscription')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => notificationApi.destroy(key)}
                >
                  {t('actions.close')}
                </Button>
              </Space>
            ),
          });

          return;
        }
      }

      await activeMutation.mutateAsync(data);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_ACTIVATE,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading: isLoading || isFetching,
    isError,
    error,
    feature,
    onDelete,
    onUpdate,
    onCreate,
    onPublic,
    onActivate,
    refetch,
  };
};
